import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Sample team members with images - replace with actual data
const teamMembers = [
    {
        label: "CEO",
        path: "/team/ceo",
        name: "John Doe",
        designation: "Chief Executive Officer",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop"
    },
    {
        label: "CTO",
        path: "/team/cto",
        name: "Jane Smith",
        designation: "Chief Technology Officer",
        image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop"
    },
    {
        label: "Design",
        path: "/team/design",
        name: "Alex Johnson",
        designation: "Design Lead",
        image: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&h=400&fit=crop"
    },
    {
        label: "Marketing",
        path: "/team/marketing",
        name: "Sarah Williams",
        designation: "Marketing Director",
        image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop"
    },
    {
        label: "Engineering",
        path: "/team/engineering",
        name: "Mike Chen",
        designation: "Lead Engineer",
        image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop"
    },
    {
        label: "Sales",
        path: "/team/sales",
        name: "Emma Davis",
        designation: "Sales Director",
        image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop"
    },
];

const NavbarWithImages = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const location = useLocation();

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, [location.pathname]);

    const handleLinkClick = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleMobileLinkClick = () => {
        setIsMenuOpen(false);
        handleLinkClick();
    };

    return (
        <header className="fixed top-0 left-0 w-full z-50 bg-gradient-to-r from-slate-950 via-black to-slate-950 backdrop-blur-xl border-b border-white/5 h-20">
            <div className="w-full flex items-center justify-between h-full px-4 sm:px-6 lg:px-8">
                {/* Left: Logo */}
                <NavLink to="/" onClick={handleLinkClick} className="flex-shrink-0 ml-4">
                    <img
                        src="/pixui-logo.svg"
                        alt="PixUI Logo"
                        className="h-10 transition-transform hover:scale-105"
                    />
                </NavLink>

                {/* Right: Desktop Navigation with Image Hover */}
                <div className="hidden lg:flex items-center h-full mr-4 gap-1">
                    <nav className="flex h-full gap-1">
                        {teamMembers.map((member, index) => (
                            <div
                                key={member.path}
                                className="relative h-full"
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                <NavLink
                                    to={member.path}
                                    onClick={handleLinkClick}
                                    className={({ isActive }) =>
                                        `relative h-full px-5 py-3 text-sm font-semibold uppercase transition-all duration-500 overflow-hidden
                    ${isActive ? "text-cyan-400" : "text-gray-300 hover:text-white"}
                    group flex items-center`
                                    }
                                >
                                    {/* Animated Background */}
                                    <span className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-blue-500/10 to-purple-500/10 scale-y-0 origin-bottom transition-all duration-500 group-hover:scale-y-100 z-0"></span>

                                    {/* Shimmer Effect */}
                                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 z-0"></span>

                                    {/* Label */}
                                    <span className="relative z-10 tracking-wider">{member.label}</span>

                                    {/* Image Card on Hover */}
                                    <AnimatePresence>
                                        {hoveredIndex === index && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                transition={{ duration: 0.3, ease: "easeOut" }}
                                                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                                                style={{
                                                    background: "linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)",
                                                    backdropFilter: "blur(20px)"
                                                }}
                                            >
                                                {/* Image */}
                                                <div className="relative h-64 overflow-hidden">
                                                    <motion.div
                                                        className="absolute inset-0 bg-cover bg-center"
                                                        style={{ backgroundImage: `url(${member.image})` }}
                                                        initial={{ scale: 1.2 }}
                                                        animate={{ scale: 1 }}
                                                        transition={{ duration: 0.6, ease: "easeOut" }}
                                                    />

                                                    {/* Gradient Overlay */}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                                                </div>

                                                {/* Info Section */}
                                                <div className="relative p-5 bg-gradient-to-b from-black/80 to-black">
                                                    <h3 className="text-xl font-bold text-white mb-1 tracking-tight">
                                                        {member.name}
                                                    </h3>
                                                    <p className="text-sm text-cyan-400 font-medium tracking-wide">
                                                        {member.designation}
                                                    </p>

                                                    {/* Decorative Line */}
                                                    <div className="mt-3 h-0.5 w-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
                                                </div>

                                                {/* Glow Effect */}
                                                <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/20 via-transparent to-purple-500/20 pointer-events-none"></div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </NavLink>
                            </div>
                        ))}
                    </nav>

                    <NavLink
                        to="/team-login"
                        onClick={handleLinkClick}
                        className="ml-6 relative group overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-6 py-2.5 rounded-lg transition-all hover:shadow-lg hover:shadow-cyan-500/50"
                    >
                        <span className="relative z-10">Team Login</span>
                        <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    </NavLink>
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden">
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="text-white focus:outline-none p-2 rounded-lg hover:bg-white/5 transition-colors"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? (
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                ></path>
                            </svg>
                        ) : (
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h16m-7 6h7"
                                ></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <motion.div
                animate={isMenuOpen ? "open" : "closed"}
                initial={false}
                variants={{
                    open: { opacity: 1, y: 0 },
                    closed: { opacity: 0, y: "-100%" },
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="lg:hidden absolute top-20 left-0 w-full bg-black/95 backdrop-blur-xl border-b border-white/5"
                style={{ pointerEvents: isMenuOpen ? "auto" : "none" }}
            >
                <nav className="flex flex-col p-4 gap-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
                    {teamMembers.map((member) => (
                        <NavLink
                            key={member.path}
                            to={member.path}
                            onClick={handleMobileLinkClick}
                            className={({ isActive }) =>
                                `relative overflow-hidden rounded-xl transition-all duration-300 ${isActive ? "ring-2 ring-cyan-400" : ""
                                }`
                            }
                        >
                            <div className="relative h-20 flex items-center">
                                {/* Background image */}
                                <div
                                    className="absolute inset-0 bg-cover bg-center"
                                    style={{ backgroundImage: `url(${member.image})` }}
                                />

                                {/* Gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>

                                {/* Content */}
                                <div className="relative z-10 flex items-center justify-between w-full px-5">
                                    <div className="text-white">
                                        <h3 className="text-base font-bold">{member.name}</h3>
                                        <p className="text-xs text-cyan-400">{member.designation}</p>
                                    </div>
                                    <span className="text-white/40 font-semibold text-xs uppercase tracking-wider">
                                        {member.label}
                                    </span>
                                </div>
                            </div>
                        </NavLink>
                    ))}

                    <NavLink
                        to="/team-login"
                        onClick={handleMobileLinkClick}
                        className="mt-2 text-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/50 transition"
                    >
                        Team Login
                    </NavLink>
                </nav>
            </motion.div>
        </header>
    );
};

export default NavbarWithImages;
